import { useCallback, useState } from "react";
import { Button } from "../../components/ui/button/button";
import { FileText, Plus, X } from "lucide-react";
import { DocumentListDialog } from "./document-list-dialog";
import { useAllDocuments } from "../../queries/documents/use-all-documents";
import { Document } from "../../odata/document/DocumentModel";
import { Card } from "../../components/ui/card/card";
import { useTranslation } from "react-i18next";

interface DocumentSelectorProps {
  value: string[];
  onChange: (docs: string[]) => void;
}

export function DocumentSelector({ value, onChange }: DocumentSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { documents } = useAllDocuments();

  const selectedDocs = documents?.filter((x) => value.includes(x.Id)) ?? [];

  const handleDocumentRemove = useCallback(
    (id: string) => {
      const docs = value.filter((x) => x !== id);
      onChange(docs ?? []);
    },
    [onChange, value]
  );

  return (
    <>
      <Button
        size="default"
        variant="outline"
        className="flex gap-2 ring-primary text-primary bg-transparent"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Plus className="h-4 w-4" />
        {t("documentSelector.select")}
      </Button>
      <DocumentListDialog
        onValueChange={onChange}
        onSelect={onChange}
        value={value}
        open={open}
        onOpenChange={setOpen}
      />
      <div role="list" className="flex flex-col gap-2">
        {selectedDocs.map((x) => (
          <DocumentCard
            document={x}
            onRemove={handleDocumentRemove}
            key={x.Id}
          />
        ))}
      </div>
    </>
  );
}

function DocumentCard({
  document,
  onRemove,
}: {
  document: Document;
  onRemove: (id: string) => void;
}) {
  const handleRemove = useCallback(() => {
    onRemove(document.Id);
  }, [document.Id, onRemove]);

  return (
    <Card className="flex items-center p-2 gap-2" role="listitem">
      <Card className="px-5 py-1">
        <FileText className="h-5 w-5" strokeWidth={1} />
      </Card>
      {document.Name}
      <span className="ml-auto" onClick={handleRemove} role="button">
        <X />
      </span>
    </Card>
  );
}
